// Files a bug report on its own when the page shows an error.
//
// Nothing is instrumented per screen: one watcher looks for the error UI the
// app already draws -- MUI error alerts (the Assign/Update/Resolve popovers,
// forms, admin pages) and Client Overview's error card -- and files it through
// the same endpoint as the "File a bug" button, with the same context and
// screenshot, but as an email to Patrick only: no DevOps ticket (auto: true).
// Form-field validation ("Required") is not a bug and is ignored.
//
// Guard rails, because every report sends an email:
//   - the error must stay on screen for STEADY_MS (a flash is not reported);
//   - one report per error per page, per browser session;
//   - at most MAX_PER_WINDOW reports per WINDOW_MS per tab;
//   - nothing inside the bug dialog itself, or anything marked
//     data-auto-bug-ignore, is ever reported;
//   - off outside production builds unless localStorage nexus.autoBug = "on".

import { captureContext, captureScreenshot, fileBugReport, redactUrl } from './bugReportService';

export const ERROR_SELECTOR = [
    '.MuiAlert-standardError', '.MuiAlert-filledError', '.MuiAlert-outlinedError',
    '.co-error-card', '[data-auto-bug="error"]',
].join(', ');
const IGNORE_SELECTOR = '[data-bug-report-exclude], [data-auto-bug-ignore]';
export const STEADY_MS = 1500;
export const MAX_PER_WINDOW = 3;
export const WINDOW_MS = 10 * 60 * 1000;
const SEEN_KEY = 'nexus.autoBug.seen';

export function autoBugEnabled() {
    try {
        const flag = window.localStorage.getItem('nexus.autoBug');
        if (flag === 'off') return false;
        if (flag === 'on') return true;
    } catch { /* storage blocked */ }
    return process.env.NODE_ENV === 'production';
}

const visible = (el) => {
    if (!el || !el.isConnected) return false;
    const r = el.getBoundingClientRect();
    const s = window.getComputedStyle(el);
    return (r.width > 0 || r.height > 0 || process.env.NODE_ENV === 'test')
        && s.visibility !== 'hidden' && s.display !== 'none';
};

export const errorText = (el) => String(el?.innerText || el?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 400);

/** Same error on the same page = same key, whatever ids or numbers it quotes. */
export function errorKey(message, path = window.location.pathname) {
    const norm = String(message).toLowerCase()
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '#')
        .replace(/\d+/g, '#');
    return `${path}|${norm}`;
}

function loadSeen() {
    try { return new Set(JSON.parse(window.sessionStorage.getItem(SEEN_KEY) || '[]')); } catch { return new Set(); }
}
function saveSeen(seen) {
    try { window.sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-200))); } catch { /* ignore */ }
}

/**
 * Starts watching. `onFiled({message, id, url})` is told about each report.
 * Returns a stop function. Test hooks: `file`, `now`, `root`.
 */
export function startAutoBugReports({
    onFiled = () => {}, file = fileAutoReport, now = () => Date.now(), root = document.body,
} = {}) {
    const seen = loadSeen();
    const pending = new Map();        // element -> timer
    const sentAt = [];
    let stopped = false;

    const consider = (el) => {
        if (stopped || pending.has(el) || el.closest(IGNORE_SELECTOR)) return;
        pending.set(el, setTimeout(async () => {
            pending.delete(el);
            if (stopped || !visible(el)) return;
            const message = errorText(el);
            if (!message) return;
            const key = errorKey(message);
            if (seen.has(key)) return;
            const t = now();
            while (sentAt.length && t - sentAt[0] > WINDOW_MS) sentAt.shift();
            if (sentAt.length >= MAX_PER_WINDOW) return;
            seen.add(key);
            saveSeen(seen);
            sentAt.push(t);
            try {
                const res = await file(message);
                onFiled({ message, id: res?.ticket?.id, url: res?.ticket?.url });
            } catch {
                // A report that could not be filed is not retried in a loop.
            }
        }, STEADY_MS));
    };

    const scan = (node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(ERROR_SELECTOR)) consider(node);
        node.querySelectorAll(ERROR_SELECTOR).forEach(consider);
    };

    scan(root);
    const observer = new MutationObserver((records) => {
        for (const r of records) {
            if (r.type === 'childList') r.addedNodes.forEach(scan);
            else if (r.target instanceof Element) scan(r.target);
        }
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
        stopped = true;
        observer.disconnect();
        pending.forEach((t) => clearTimeout(t));
        pending.clear();
    };
}

export async function fileAutoReport(message) {
    const [context, screenshot] = await Promise.all([captureContext(), captureScreenshot()]);
    return fileBugReport({
        description: `[Auto-filed] The page showed an error: "${message}" on ${redactUrl(window.location.href)}`,
        expected: 'No error. Nobody typed this report: it was filed automatically when the error appeared, '
            + 'with the page context and a screenshot from that moment.',
        context: { ...context, 'Auto-filed': { 'Error shown': message } },
        screenshot,
        auto: true,
    });
}
